import { Box } from "@rebass/grid";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as authUtils from "@rentiohq/shared-frontend/dist/reduxV2/auth/auth.utils";
import * as enabledAccountActions from "@rentiohq/shared-frontend/dist/reduxV2/enabledAccount/enabledAccount.actions";
import { IAccount } from "@rentiohq/shared-frontend/dist/types/auth.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  IOptionListOption,
  Loading,
  Modal,
  AsyncSelect,
  TextStyle,
  Select,
} from "@rentiohq/web-shared/dist/components";
import EnabledAccountsSelectForceRefresh from "components/EnabledAccountsSelectForceRefresh";
import React from "react";
import { useDispatch } from "react-redux";
import { setPendingAccountSwitch } from "utils/accountSwitch.utils";
import {
  getDisplayName,
  sortOptionsByInput,
} from "./EnabledAccountsSelect.utils";

const QUERY_DELAY = 500;
const QUERY_SEARCH_LIMIT = 2;

export type IEnabledAccount = IAccount;

interface IProps {
  showModal: boolean;
  onClose: () => void;
}

const EnabledAccountsSelect = (props: IProps) => {
  const { showModal, onClose } = props;

  // Redux
  const dispatch = useDispatch();
  const { user, isMonitor } = authHooks.useSelf();
  // State
  const [selectedAccountId, setSelectedAccountId] = React.useState<
    number | undefined
  >();
  const [input, setInput] = React.useState("");
  const [enabledAccounts, setEnabledAccounts] = React.useState<
    IEnabledAccount[]
  >([]);
  const [isFetching, setIsFetching] = React.useState(false);
  const timerRef = React.useRef<number | null>(null);

  const fetchAccounts = async (
    rawQuery?: string,
  ): Promise<IEnabledAccount[] | undefined> => {
    if (!showModal || !user) return;

    const trimmed = rawQuery?.trim();

    const searchQuery =
      trimmed && trimmed.length >= QUERY_SEARCH_LIMIT ? trimmed : undefined;

    if (isMonitor && !searchQuery) return;

    return new Promise<IEnabledAccount[] | undefined>(resolve => {
      setIsFetching(true);

      dispatch(
        enabledAccountActions.getEnabledAccountsStart.getAction({
          userId: user.id,
          search: searchQuery,
          onSuccess: accounts => {
            setIsFetching(false);

            if (!isMonitor) {
              setEnabledAccounts(accounts);
            }

            resolve(accounts);
          },
          onFailure: () => {
            setIsFetching(false);
            resolve(undefined);
          },
        }),
      );
    });
  };

  const handleLoadOptions = (
    input: string,
    callback: (opts: any[]) => void,
  ) => {
    if (!user) {
      callback([]);
      return;
    }

    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    timerRef.current = window.setTimeout(async () => {
      const accounts = await fetchAccounts(input);

      const options = (accounts || []).map(item => ({
        label: getDisplayName(item, user),
        value: item.id,
      }));
      callback(options);
      timerRef.current = null;
    }, QUERY_DELAY);
  };

  const handleSelectAccount = (option: IOptionListOption | null) => {
    if (!option) {
      setSelectedAccountId(undefined);
      return;
    }

    if (typeof option.value !== "number") return;
    setSelectedAccountId(option.value);
  };

  const handleUseSelectedAccount = () => {
    if (!user) {
      return;
    }

    const accountId = selectedAccountId || user.activeAccountId;
    if (!accountId) {
      return;
    }

    dispatch(
      enabledAccountActions.useEnabledAccountStart.getAction({
        userId: user.id,
        enabledAccountId: accountId,
        onSuccess: async () => {
          onClose();

          await authUtils.setAccessTokenLastAccountSelectDate(new Date());

          // The reload below boots on the persisted user of the account we are
          // leaving. Record where we are going, so App can tell that user is
          // stale and hold off the fetches keyed on its brokerId.
          setPendingAccountSwitch(accountId);

          if (isMonitor) {
            window.location.href = `${window.location.origin}/monitor`;
          } else {
            window.location.href = window.location.origin;
          }
        },
        onFailure: error => {
          showAlert({
            type: "error",
            error,
          });
        },
      }),
    );
  };

  React.useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (showModal && !isMonitor) {
      fetchAccounts();
    }
  }, [showModal, isMonitor]);

  if (!user) {
    return null;
  }

  return (
    <>
      {showModal && (
        <Modal
          width="medium"
          shouldCloseOnOverlayClick={true}
          hasDismiss={true}
          onClose={onClose}
          heading={getLocalizedText(
            "modal.enabled_accounts_select.select.title",
          )}
          actions={
            !!selectedAccountId
              ? [
                  {
                    content: getLocalizedText("system.switch"),
                    onClick: handleUseSelectedAccount,
                    appearance: "primary",
                  },
                ]
              : []
          }
        >
          {isMonitor ? (
            <AsyncSelect
              cacheOptions
              id="user-select"
              label={getLocalizedText(
                "modal.enabled_accounts_select.select.dropdown_label",
              )}
              value={selectedAccountId || user?.activeAccountId}
              onChange={handleSelectAccount}
              loadOptions={handleLoadOptions}
              blurInputOnSelect
              inputValue={undefined}
              placeholder={getLocalizedText(
                "modal.enabled_accounts_select.select.dropdown_label",
              )}
            />
          ) : (
            <Select
              id={"user-select"}
              label={getLocalizedText(
                "modal.enabled_accounts_select.select.dropdown_label",
              )}
              onInputChange={(value: string) => setInput(value)}
              value={selectedAccountId || user.activeAccountId}
              onChange={handleSelectAccount}
              options={sortOptionsByInput(
                enabledAccounts?.map(item => ({
                  label: getDisplayName(item, user),
                  value: item.id,
                })),
                input,
              )}
            />
          )}
          {isFetching && <Loading />}

          <Box mt={1}>
            <TextStyle variation="subdued">
              {getLocalizedText("enabled_accounts.search_restriction.message")}
            </TextStyle>
          </Box>
        </Modal>
      )}
      <EnabledAccountsSelectForceRefresh />
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default EnabledAccountsSelect;
